/**
 * Evacuation (FEAT-038 wave 1) — the bytes come out of the records, once.
 *
 * Two stores on Nation's phone hold a whole video INSIDE the record that
 * describes it: `momento-drafts/captures` and `momento-uploads/pending-uploads`.
 * An ArrayBuffer is not file-backed, so reading such a record for ANY reason
 * deserialises the video, and the first three seconds of a launch walk those
 * stores about twenty-five times — counting drafts, refreshing the pill,
 * resuming uploads. Six clips is gigabytes of transient allocation while the
 * camera is already asking for 4K, and iOS kills the app 1.6 seconds in.
 *
 * This runs BEFORE either store is opened by its service (both gate `init()`
 * on `evacuation.done()`), and rewrites every fat record thin: the bytes move
 * to `mediaStore`, the record keeps its labels plus a `mediaId`. After it, no
 * walk of either store can materialise a video, whatever the reader wanted.
 *
 * The single-record rule: one record's bytes are resident at a time, for as
 * long as it takes to copy them out — never two, and never a store's worth.
 *
 * Crash safety, because this runs on the device that was crashing:
 *
 * - A TOMBSTONE is written before a record is read and cleared after it is
 *   written back. Finding one at boot means that record was resident when the
 *   app died — it is PARKED (never touched again) rather than retried into the
 *   same crash. Parking is the difference between an app that opens with one
 *   unmovable draft and an app that does not open.
 * - An ERROR is not a kill. A record whose copy throws (quota, a torn blob)
 *   stays fat, its tombstone is cleared, and the next launch tries again.
 * - `mediaStore` commits its meta row LAST, so a kill mid-copy leaves only
 *   orphan chunks, which the sweep at the end collects.
 *
 * It imports `mediaStore` and nothing else on purpose: importing either
 * service would be a cycle (they gate on this) and would run their module-load
 * side effects — including the store opens this exists to happen before.
 */
import { mediaStore, type NewMediaMeta } from './mediaStore';
import type { DrawingStroke, MomentOverlays } from '../../types';

const DRAFTS_DB = 'momento-drafts';
const DRAFTS_STORE = 'captures';
const OUTBOX_DB = 'momento-uploads';
const OUTBOX_STORE = 'pending-uploads';

/** An outbox row's media id. Drafts use the row id unchanged — a draft's id IS
 * its capture id — so the two namespaces need one prefix between them. */
export const OUTBOX_MEDIA_PREFIX = 'outbox-';

const TOMBSTONE_KEY = 'evacuate-tombstone';
const FAILED_KEY = 'evacuate-failed';

const OPEN_TIMEOUT_MS = 5000;

type LegacyStore = typeof DRAFTS_STORE | typeof OUTBOX_STORE;

interface Tombstone {
  key: string;
  store: LegacyStore;
  id: string;
}

interface FailedSet {
  key: string;
  /** `<store>/<id>` slugs — records that were resident when the app died. */
  ids: string[];
}

/** Either store's record, read structurally: this file is deliberately not
 * coupled to the two shapes it is retiring. */
type FatRow = Record<string, unknown> & {
  id: string;
  bytes?: ArrayBuffer;
  blobType?: string;
  legacyBlob?: Blob;
  mediaBlob?: Blob;
};

// ---- raw IDB, without either service ---------------------------------------

function reqOf<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Open a legacy database WITHOUT creating it. `indexedDB.databases()` is what
 * makes that possible: a versionless open of an absent name creates an empty
 * database, and an empty `momento-drafts` would then take its v1→v2 upgrade
 * path on a phone that never had a draft.
 *
 * Every failure — absent, blocked, slow, erroring — resolves null. A store we
 * cannot open is a store we leave alone.
 */
async function openLegacy(name: string, store: string): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return null;
  if (typeof indexedDB.databases === 'function') {
    try {
      const known = await indexedDB.databases();
      if (!known.some((entry) => entry.name === name)) return null;
    } catch {
      // Not available (older Safari) — fall through to the tolerant open.
    }
  }
  return new Promise<IDBDatabase | null>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const finish = (db: IDBDatabase | null) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      resolve(db);
    };
    timer = setTimeout(() => finish(null), OPEN_TIMEOUT_MS);

    const request = indexedDB.open(name);
    request.onsuccess = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(store)) {
        db.close();
        finish(null);
        return;
      }
      finish(db);
    };
    request.onerror = () => finish(null);
    request.onblocked = () => finish(null);
  });
}

function keysOf(db: IDBDatabase, store: string): Promise<string[]> {
  return reqOf(
    db.transaction(store, 'readonly').objectStore(store).getAllKeys()
  ) as Promise<string[]>;
}

function rowOf(db: IDBDatabase, store: string, id: string): Promise<FatRow | undefined> {
  return reqOf(db.transaction(store, 'readonly').objectStore(store).get(id)) as Promise<
    FatRow | undefined
  >;
}

function putRow(db: IDBDatabase, store: string, row: FatRow): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('put aborted'));
  });
}

// ---- record shapes ---------------------------------------------------------

const mediaIdFor = (store: LegacyStore, id: string): string =>
  store === OUTBOX_STORE ? OUTBOX_MEDIA_PREFIX + id : id;

const slugOf = (store: LegacyStore, id: string): string => `${store}/${id}`;

const isFat = (row: FatRow): boolean =>
  !!row.bytes || !!row.legacyBlob || !!row.mediaBlob;

function blobOf(row: FatRow): Blob {
  if (row.bytes) return new Blob([row.bytes], { type: row.blobType || '' });
  return (row.legacyBlob ?? row.mediaBlob) as Blob;
}

function toMediaMeta(store: LegacyStore, row: FatRow, mediaId: string): NewMediaMeta {
  const mimeType =
    (row.blobType as string | undefined) ||
    (row.legacyBlob as Blob | undefined)?.type ||
    (row.mediaBlob as Blob | undefined)?.type ||
    '';
  const common = {
    id: mediaId,
    mediaType: (row.mediaType as 'photo' | 'video') ?? 'photo',
    mimeType,
    facing: row.facing as 'user' | 'environment' | undefined,
    hd: row.hd as boolean | undefined,
    overlays: (row.overlays as MomentOverlays | null) ?? null,
    createdAt: (row.createdAt as number) ?? Date.now(),
  };
  if (store === OUTBOX_STORE) {
    // An outbox row has no owner field — the queue is per-device, not per-user
    // — and no dimensions. Wave 2 gives queued media a real owner; until then
    // 0 is the sentinel that keeps it out of every user's budget.
    return {
      ...common,
      userId: 0,
      width: 0,
      height: 0,
      kind: 'single',
      textBars: [],
      drawingStrokes: [],
    };
  }
  return {
    ...common,
    userId: (row.userId as number) ?? 0,
    width: (row.width as number) ?? 0,
    height: (row.height as number) ?? 0,
    duration: row.duration as number | undefined,
    kind: (row.kind as 'single' | 'batch-shot') ?? 'single',
    batchId: row.batchId as string | undefined,
    batchIndex: row.batchIndex as number | undefined,
    textBars: (row.textBars as { text: string; y: number }[]) ?? [],
    drawingStrokes: (row.drawingStrokes as DrawingStroke[]) ?? [],
  };
}

/** The same record minus its media. Thumbnails stay — they are the ~20 KB the
 * queue surfaces are allowed to read — and so does blobType, as a mime cache. */
function thinned(row: FatRow, mediaId: string, size: number): FatRow {
  const next: FatRow = { ...row, mediaId, sizeBytes: size };
  delete next.bytes;
  delete next.legacyBlob;
  delete next.mediaBlob;
  return next;
}

// ---- the run ---------------------------------------------------------------

/**
 * Park whatever was resident when the app last died, so this launch does not
 * walk into the same wall. The record stays fat and is skipped from now on;
 * any committed half-copy of it is reclaimed.
 */
async function parkPending(failed: Set<string>): Promise<void> {
  const tomb = await mediaStore.kvGet<Tombstone>(TOMBSTONE_KEY);
  if (!tomb) return;
  console.warn('[Evacuate] parking a record that was open when the app died:', tomb);
  failed.add(slugOf(tomb.store, tomb.id));
  await mediaStore.kvSet(FAILED_KEY, { ids: [...failed] });
  await mediaStore.remove([mediaIdFor(tomb.store, tomb.id)], 'discarded').catch(() => {});
  await mediaStore.kvDelete(TOMBSTONE_KEY);
}

async function evacuateStore(
  db: IDBDatabase,
  store: LegacyStore,
  failed: Set<string>
): Promise<void> {
  // Keys only. Asking this store for its rows is the disease.
  const ids = await keysOf(db, store);
  for (const id of ids) {
    if (failed.has(slugOf(store, id))) continue;
    try {
      // Before the read, not after: the tombstone's whole job is to describe
      // the record whose deserialisation may be what kills us.
      await mediaStore.kvSet(TOMBSTONE_KEY, { store, id });
      const row = await rowOf(db, store, id);
      if (!row || !isFat(row)) {
        await mediaStore.kvDelete(TOMBSTONE_KEY);
        continue;
      }
      const mediaId = mediaIdFor(store, id);
      const blob = blobOf(row);
      // A record that CLAIMS media must never be thinned into pointing at
      // nothing: whatever we could not read is still whole where it is.
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error('media on this record could not be read');
      }
      // A run that died between the copy and the write-back left a committed
      // copy behind. The record is still fat, so that copy is stale.
      if (await mediaStore.meta(mediaId)) {
        await mediaStore.remove([mediaId], 'discarded');
      }
      await mediaStore.saveBlob(toMediaMeta(store, row, mediaId), blob, {
        bypassBudget: true,
      });
      await putRow(db, store, thinned(row, mediaId, blob.size));
      await mediaStore.kvDelete(TOMBSTONE_KEY);
    } catch (err) {
      // Not a kill — a refusal. The record stays fat and is retried next
      // launch; saveBlob's abort already took any partial chunks with it.
      console.error(`[Evacuate] ${slugOf(store, id)} stays fat:`, err);
      await mediaStore.kvDelete(TOMBSTONE_KEY).catch(() => {});
    }
    // Yield between records: this runs ahead of first paint, and the GC needs
    // a turn to release the blob before the next one is read.
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}

export async function runEvacuation(): Promise<void> {
  const drafts = await openLegacy(DRAFTS_DB, DRAFTS_STORE);
  const outbox = await openLegacy(OUTBOX_DB, OUTBOX_STORE);
  // Neither store exists (a fresh install): there is nothing to move and
  // nothing to sweep against, and the media store need not even be opened.
  if (!drafts && !outbox) return;

  try {
    const failed = new Set(
      (await mediaStore.kvGet<FailedSet>(FAILED_KEY))?.ids ?? []
    );
    await parkPending(failed);

    if (drafts) await evacuateStore(drafts, DRAFTS_STORE, failed);
    if (outbox) await evacuateStore(outbox, OUTBOX_STORE, failed);

    // No unreferenced-media sweep here any more (FEAT-038 wave 2). It compared
    // media ids against the two legacy stores' keys — and wave 2 EMPTIES those
    // stores, so on the very next launch every id would have read as
    // unreferenced and every moment on the phone would have been deleted.
    // Ownership now belongs to the media rows themselves (`draft`) and to the
    // ledger; `runQueueMigration` reclaims what neither claims.
    await mediaStore.sweepOrphanChunks();
  } finally {
    // BEFORE this resolves: captureDrafts opens momento-drafts at version 2
    // the moment it does, and a connection still held here would block that
    // open behind a versionchange that never completes.
    drafts?.close();
    outbox?.close();
  }
}

/**
 * The gate every reader of those two stores waits on. Memoized, and it NEVER
 * rejects: a failed evacuation means the records stay fat this launch (v2.59.0
 * behaviour), not an app that refuses to boot.
 *
 * It runs in recovery mode too. Recovery mode exists because the last two
 * launches died; this is the cure for what killed them, and it is the one boot
 * task that parks its own poison rather than repeating them.
 */
class Evacuation {
  private promise: Promise<void> | null = null;

  done(): Promise<void> {
    if (!this.promise) {
      this.promise =
        import.meta.env.MODE === 'test'
          ? Promise.resolve()
          : runEvacuation().catch((err) =>
              console.error('[Evacuate] failed — records stay fat this launch:', err)
            );
    }
    return this.promise;
  }

  /** Tests: drive the gate without running a real evacuation. */
  _setPromiseForTests(promise: Promise<void>): void {
    this.promise = promise;
  }

  _resetForTests(): void {
    this.promise = null;
  }
}

export const evacuation = new Evacuation();
